import React, { useState, useCallback, useMemo, useEffect } from "react";

import Image from "@sproutsocial/seeds-react-image";
import type { TypeAvatarProps } from "./AvatarTypes";

import { Container, AvatarText } from "./styles";
import { getInitials, getAvatarColor } from "./utils";

const defaultType = "neutral";

const AvatarStyled = React.forwardRef<HTMLDivElement, TypeAvatarProps>(
  (
    {
      appearance = "circle",
      name = "",
      src,
      type = defaultType,
      size = "40px",
      bg,
      color,
      initials,
      "aria-hidden": ariaHidden,
      ...rest
    }: TypeAvatarProps,
    ref
  ) => {
    const colorType = getAvatarColor(name, type);
    const [imageFailedLoading, setImageFailedLoading] = useState(false);

    useEffect(() => {
      // If the src changes, we need to invalidate the image failed to load flag
      setImageFailedLoading(false);
    }, [src]);

    const displayInitials = useMemo(
      () => initials || getInitials(name),
      [initials, name]
    );
    const handleError = useCallback(() => {
      setImageFailedLoading(true);
    }, [setImageFailedLoading]);

    // Font size for initials is half the size of the avatar, rounded down.
    const fontSize = Math.floor(Number(size.replace("px", "")) * 0.4);

    const ariaProps = {
      "aria-hidden": ariaHidden,
      alt: ariaHidden ? "" : name,
    };

    return (
      <Container
        ref={ref}
        size={size}
        overflow="hidden"
        borderRadius={appearance === "leaf" ? "40% 0 40% 0" : "50%"}
        position="relative"
        display="flex"
        flexShrink={0}
        justifyContent="center"
        alignItems="center"
        title={name || "?"}
        bg={bg}
        $type={colorType}
        data-qa-user-avatar={name}
        $displayFallback={!src || imageFailedLoading}
        {...rest}
      >
        {!src || imageFailedLoading ? (
          <AvatarText
            lineHeight={size}
            fontWeight="semibold"
            fontSize={fontSize}
            type={colorType}
            color={color}
            // If the image fails to load, the avatar is decorative only and should be hidden from assistive technologies.
            // There is a title of the full name
            aria-hidden
          >
            {displayInitials}
          </AvatarText>
        ) : (
          <Image
            {...ariaProps}
            width="auto"
            height="100%"
            src={src}
            onError={handleError}
            m={0}
          />
        )}
      </Container>
    );
  }
);

AvatarStyled.displayName = "Avatar";
export default AvatarStyled;
